import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface TestimonialCardProps {
  quote: string;
  role: string;
  name: string;
  location: string;
  results: string;
  image: string;
  alt: string;
  delay?: string;
}

function TestimonialCard({ quote, role, name, location, results, image, alt, delay }: TestimonialCardProps) {
  return (
    <div className="glass-card rounded-2xl p-8 hover:shadow-xl transition-all duration-300 reveal" style={{ animationDelay: delay }}>
      {/* Farmer Photo */}
      <div className="mb-6 relative w-full h-64 rounded-xl overflow-hidden">
        <Image
          src={image}
          alt={alt}
          fill
          className="object-cover" />
        
      </div>

      {/* Quote Icon */}
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
        <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-primary" />
      </div>

      {/* Quote */}
      <p className="text-lg text-foreground leading-relaxed mb-4">"{quote}"</p>

      {/* Results Badge */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <p className="text-sm font-semibold text-green-800 mb-1">Results Achieved:</p>
        <p className="text-sm text-green-700">{results}</p>
      </div>

      {/* Author */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
          <Icon name="UserIcon" size={24} className="text-primary" />
        </div>
        <div>
          <div className="font-bold text-foreground">{name}</div>
          <div className="text-sm text-muted-foreground">{role}</div>
          <div className="text-xs text-primary font-medium mt-1">{location}</div>
        </div>
      </div>
    </div>);

}

export default function TestimonialsSection() {
  const testimonials = [
  {
    id: 'testimonial_1',
    quote: 'Since switching to Bloom Layer Concentrate, our egg production improved significantly. The shells are stronger and breakages have reduced by 40%. Our customers notice the difference in egg quality.',
    role: 'Commercial Layer Farm Owner',
    name: 'Sarah Namukasa',
    location: 'Wakiso District, Central Uganda',
    results: '25% increase in egg production, 40% reduction in shell breakages, improved feed conversion ratio from 2.8 to 2.3',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11d7ebe88-1771322819300.png",
    alt: 'Female farmer in green shirt inspecting eggs at commercial layer farm in Uganda'
  },
  {
    id: 'testimonial_2',
    quote: 'Our broilers reached market weight nearly a week earlier using Bloom Broiler Starter and Finisher. The feed conversion ratio has clearly improved and mortality rates dropped significantly.',
    role: 'Broiler Farm Manager',
    name: 'David Okello',
    location: 'Mbarara, Western Uganda',
    results: '7 days faster growth to market weight, FCR improved from 1.9 to 1.6, mortality reduced from 8% to 3%',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c6bc3f27-1770040084525.png",
    alt: 'Male farmer in blue overalls examining healthy broiler chickens in modern poultry house'
  },
  {
    id: 'testimonial_3',
    quote: 'Consistency is what makes Bloom different. Every batch performs the same. My customers keep coming back because they see real results. Sales have grown 60% since we started stocking Bloom feeds.',
    role: 'Agro-Vet Distributor',
    name: 'James Mugisha',
    location: 'Kampala Central Business District',
    results: '60% sales growth in 6 months, zero quality complaints, 95% customer retention rate',
    image: "https://images.unsplash.com/photo-1605532923000-7c46d95f5602",
    alt: 'Agro-vet shop owner standing in front of stocked animal feed shelves in Kampala store'
  }];


  return (
    <section className="py-section px-6 bg-gradient-to-b from-white to-green-50/30">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <p className="text-primary font-bold uppercase tracking-wider text-sm mb-4">Success Stories</p>
          <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground mb-6">
            Real results from real farmers <br />
            <span className="gradient-text">across Uganda</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Discover how Bloom Animal Feeds is transforming poultry farming with proven results from layer farms, broiler producers, and agro-vet distributors.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) =>
          <TestimonialCard
            key={testimonial.id}
            quote={testimonial.quote}
            role={testimonial.role}
            name={testimonial.name}
            location={testimonial.location}
            results={testimonial.results}
            image={testimonial.image}
            alt={testimonial.alt}
            delay={`${index * 0.1}s`} />

          )}
        </div>
      </div>
    </section>);

}